/**
 * Layout-only page: shows the trading layout (ShowcaseLayout) without any
 * configuration UI. All settings are hardcoded below for easy editing.
 */
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { toast } from "sonner";
import type { ResponsiveLayouts, Layout } from "react-grid-layout";
import {
  ShowcaseLayout,
  type LayoutItem,
  TradingViewWidget,
  Orderbook002,
  TradePrice,
  TradeOperation,
  LeverageModal,
  NumberPad,
  generateLayout,
  TickerBar,
  TabSection,
  MenuBar,
  SettingsModal,
  DEFAULT_SETTINGS,
  type SettingsModalState,
  type TabSectionTabId,
  TransferModal,
  type TransferModalConfirmParams,
  WalletTransferModal,
  type WalletTransferConfirmParams,
  type WalletTransferAssetId,
  OnboardModal,
} from "../components";
import { useMarkets } from "../hooks/useMarkets";
import { useOnboard } from "../hooks/useOnboard";
import { useBucketCollections } from "../hooks/useBucketCollections";
import { useFiClient } from "../hooks/useFiClient";
import { DEFAULT_JITTER } from "../utils/orderbookEnhancement";
import { DEFAULT_MNEMONIC } from "../constants";
import { demoDesignatedLayouts } from "../configs/layoutConfig";
import {
  SubscriptionProvider,
  useSubscription,
} from "../context/SubscriptionContext";
import { capitalize } from "../components/smartlayout/util";
import { createErrorCopyAction } from "../components/ui/Toaster";
import { formatCurrency } from "../sdk/utils/display";
import { Bucket, getDisplayMarginType, InfoClient } from "../sdk";
import type {
  ExtractToBucketResponse,
  BucketTransferResponse,
  WithdrawFromBucketResponse,
} from "../sdk/types/api";
import { toNumberFromSatoshi } from "../sdk/utils/math";

const ONBOARD_COMPLETED_KEY = "onboard_completed";

// =============================================================================
// HARDCODED CONFIGURATION – edit these to change behavior
// =============================================================================
const CONFIG = {
  baseURL: "https://router.morpheum.io",
  wsURL: "wss://router.morpheum.io/websocket",
  layoutConfig: demoDesignatedLayouts,
  compactType: "vertical" as const,
  allStatic: true,
  fullWidth: false,
  orderbookEnhancement: {
    enabled: true,
    jitter: DEFAULT_JITTER,
    fakePending: {
      maxFakeLevelsPerSide: 3,
      minLifetimeMs: 2000,
      maxLifetimeMs: 8000,
      volumeScalePct: 0.8,
      minAddDelayMs: 1500,
      maxAddDelayMs: 5000,
    },
  },
  orderbookMaxDepth: 20,
  refreshInterval: 5000,
  tradingViewSymbol: "BINANCE:BTCUSDT",
  tradingViewInterval: "5",
  /** [horizontal, vertical] gap between layout boxes in pixels */
  layoutMargin: [5, 5] as const,
} as const;

// =============================================================================
// LAYOUT GRID CONSTANTS
// =============================================================================
const ROW_HEIGHT = 30;
const COLS = 12;
/** Column counts per breakpoint (must match ShowcaseLayout default / props) */
const CURRENT_COLS: Record<string, number> = {
  lg: 12,
  md: 10,
  sm: 6,
  xs: 4,
  xxs: 2,
};

function LayoutOnlyContent() {
  const [selectedBucketId, setSelectedBucketId] = useState<string>("--");
  const [selectedBucketBalance, setSelectedBucketBalance] = useState<
    number | undefined
  >(undefined);
  const [isLeverageModalOpen, setIsLeverageModalOpen] = useState(false);
  const [tempLeverage, setTempLeverage] = useState(10);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [settingsState, setSettingsState] = useState<SettingsModalState>(DEFAULT_SETTINGS);
  const [isNumberCalculatorOpen, setIsNumberCalculatorOpen] = useState(false);
  const [numberCalculatorValue, setNumberCalculatorValue] = useState(0);
  const [numberCalculatorMax, setNumberCalculatorMax] = useState(0);
  const [tabSectionActiveTab, setTabSectionActiveTab] =
    useState<TabSectionTabId>("orders");
  const showcaseLayoutRef = useRef<ShowcaseLayout>(null);
  const tradeOperationRef =
    useRef<import("../components/TradeOperation").TradeOperationRef>(null);

  const [currentBreakpoint, setCurrentBreakpoint] = useState<string>("lg");
  const [isLayoutMounted, setIsLayoutMounted] = useState(false);
  const hasTriggeredToggleRef = useRef(false);
  const prevLayoutRef = useRef<LayoutItem[]>([]);

  const initialLayout = useMemo(
    () => generateLayout(CONFIG.layoutConfig, 0),
    [],
  );

  const [layout, setLayout] = useState<LayoutItem[]>(() =>
    generateLayout(CONFIG.layoutConfig, 0),
  );

  useEffect(() => {
    setLayout(initialLayout);
    prevLayoutRef.current = initialLayout;
    setIsLayoutMounted(false);
    hasTriggeredToggleRef.current = false;
  }, [initialLayout]);

  const [sessionToken, setSessionToken] = useState(() =>
    localStorage.getItem("authToken") || "",
  );

  const {
    markets,
    selectedMarketId,
    loadingMarkets,
    getError,
  } = useMarkets({
    baseURL: CONFIG.baseURL,
    sessionToken,
    autoFetchMarkets: true,
  });

  const { toggleEnabled } = useSubscription();
  const {
    mnemonic,
    walletAddress,
    authToken: onboardAuthToken,
    setMnemonic: setOnboardMnemonic,
    onboard,
    setAccountData,
    resetAll: onboardResetAll,
  } = useOnboard(CONFIG.baseURL);

  const [showOnboardModal, setShowOnboardModal] = useState(() =>
    !localStorage.getItem(ONBOARD_COMPLETED_KEY),
  );

  useEffect(() => {
    if (!mnemonic && !showOnboardModal && !walletAddress) {
      setOnboardMnemonic(DEFAULT_MNEMONIC);
    }
  }, [mnemonic, walletAddress, setOnboardMnemonic, showOnboardModal]);

  const handleMnemonicConnect = useCallback(
    async (mnemonicPhrase: string, _address: string) => {
      await setOnboardMnemonic(mnemonicPhrase);
      await onboard(mnemonicPhrase, CONFIG.baseURL);
      localStorage.setItem(ONBOARD_COMPLETED_KEY, "1");
      setShowOnboardModal(false);
    },
    [setOnboardMnemonic, onboard],
  );

  const handleWalletExtensionConnect = useCallback((address: string) => {
    setAccountData("", address);
    localStorage.setItem(ONBOARD_COMPLETED_KEY, "1");
    setShowOnboardModal(false);
  }, [setAccountData]);

  useEffect(() => {
    prevLayoutRef.current = layout;
  }, [layout]);

  useEffect(() => {
    setSessionToken(onboardAuthToken ?? "");
    if (onboardAuthToken) {
      localStorage.setItem("authToken", onboardAuthToken);
    } else {
      localStorage.removeItem("authToken");
    }
  }, [onboardAuthToken]);

  const { setAuthToken, setWalletAddress, client: subscriptionClient } = useSubscription();
  useEffect(() => {
    setAuthToken(sessionToken || undefined);
  }, [sessionToken, setAuthToken]);
  useEffect(() => {
    setWalletAddress(walletAddress || undefined);
  }, [walletAddress, setWalletAddress]);

  const {
    collections,
    spotWalletBalance,
    refreshBucketBalance,
  } = useBucketCollections({
    subscriptionClient: subscriptionClient ?? undefined,
    baseURL: CONFIG.baseURL,
    authToken: sessionToken,
    walletAddress: walletAddress ?? "",
    refreshInterval: CONFIG.refreshInterval,
  });

  const { client: exchangeClient, isReady: exchangeReady, error: exchangeError } = useFiClient({
    baseURL: CONFIG.baseURL,
    mnemonic: mnemonic ?? "",
  });

  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [transferFrom, setTransferFrom] = useState<string>("");
  const [transferTo, setTransferTo] = useState<string>("");
  const [transferAmount, setTransferAmount] = useState<number>(10);
  const [createBucketAmount, setCreateBucketAmount] = useState<number>(10);
  const [createBucketAsset, setCreateBucketAsset] = useState<string>("");
  const [createMarginType, setCreateMarginType] = useState<"cross" | "isolated">("cross");
  const [transferMarginType, setTransferMarginType] = useState<"cross" | "isolated">("cross");
  const [transferActiveTab, setTransferActiveTab] = useState<"transfer" | "create">("transfer");
  const [isTransferring, setIsTransferring] = useState<boolean>(false);
  const loadingToastIdRef = useRef<number>(0);

  const [isWalletTransferModalOpen, setIsWalletTransferModalOpen] = useState(false);
  const [walletTransferTo, setWalletTransferTo] = useState("");
  const [walletTransferAsset, setWalletTransferAsset] = useState<WalletTransferAssetId>("USDC");
  const [walletTransferAmount, setWalletTransferAmount] = useState(10);
  const [walletTransferMemo, setWalletTransferMemo] = useState("");
  const [walletTransferBalance, setWalletTransferBalance] = useState(0);
  const infoClientRef = useRef<InfoClient | null>(null);

  useEffect(() => {
    if (exchangeError) {
      const msg = exchangeError.message;
      console.error("Failed to initialize FiClient:", exchangeError);
      toast.error("Exchange client initialization failed", {
        description: msg,
        action: createErrorCopyAction(msg),
      });
    }
  }, [exchangeError]);

  useEffect(() => {
    infoClientRef.current = new InfoClient({
      baseURL: CONFIG.baseURL,
      sessionToken: sessionToken ?? undefined,
    });
  }, [sessionToken]);

  const fetchWalletTransferBalance = useCallback(async () => {
    if (!walletAddress?.trim() || !infoClientRef.current) return;
    try {
      const balance = await infoClientRef.current.getBalanceDecimal(
        walletAddress,
        walletTransferAsset,
      );
      const available =
        typeof balance.available === "number"
          ? balance.available
          : balance.availableBalance
            ? toNumberFromSatoshi(balance.availableBalance)
            : 0;
      setWalletTransferBalance(available);
    } catch {
      setWalletTransferBalance(0);
    }
  }, [walletAddress, walletTransferAsset]);

  useEffect(() => {
    if (isWalletTransferModalOpen && walletAddress?.trim()) {
      fetchWalletTransferBalance();
    }
  }, [isWalletTransferModalOpen, walletAddress, walletTransferAsset, fetchWalletTransferBalance]);

  const assetSymbolToCollateralIndex = useCallback((symbol: string): number => {
    const m: Record<string, number> = { USDC: 1, USDT: 2 };
    return m[symbol?.toUpperCase()] ?? 1;
  }, []);

  const findDormantOrEmptyBucket = useCallback((
    marginType: "cross" | "isolated",
    collateralAsset: string,
  ): Bucket | null => {
    const normalizedMarginType = marginType.toLowerCase();
    const normalizedAsset = collateralAsset.toUpperCase();
    const expectedIndex =
      normalizedAsset &&
      String(assetSymbolToCollateralIndex(normalizedAsset));
    return (
      collections.find((bucket) => {
        const bucketMarginType = getDisplayMarginType(bucket)?.toLowerCase();
        const isDormant = bucket.status?.toLowerCase() === "dormant";
        const isEmpty = Number(bucket.totalEquity || 0) <= 0;
        const matchesMarginType = bucketMarginType === normalizedMarginType;
        const matchesCollateralAsset = expectedIndex
          ? bucket.collateralAssetIndex === expectedIndex
          : true;
        return (isDormant || isEmpty) && matchesMarginType && matchesCollateralAsset;
      }) || null
    );
  }, [collections, assetSymbolToCollateralIndex]);

  const handleTransferClick = useCallback(() => {
    setIsTransferModalOpen(true);
    setTransferFrom("");
    setTransferTo("");
    setTransferAmount(10);
    setCreateBucketAmount(10);
    setCreateBucketAsset("");
    setCreateMarginType("cross");
    setTransferMarginType("cross");
    setTransferActiveTab("transfer");
  }, []);

  const handleTransferCancel = useCallback(() => {
    setIsTransferModalOpen(false);
    setTransferFrom("");
    setTransferTo("");
    setTransferAmount(10);
    setCreateBucketAmount(10);
    setCreateBucketAsset("");
    setCreateMarginType("cross");
    setTransferMarginType("cross");
    setTransferActiveTab("transfer");
  }, []);

  const handleWalletTransferClick = useCallback(() => {
    setIsWalletTransferModalOpen(true);
    setWalletTransferTo("");
    setWalletTransferAsset("USDC");
    setWalletTransferAmount(10);
    setWalletTransferMemo("");
  }, []);

  const handleWalletTransferCancel = useCallback(() => {
    setIsWalletTransferModalOpen(false);
    setWalletTransferTo("");
    setWalletTransferAsset("USDC");
    setWalletTransferAmount(10);
    setWalletTransferMemo("");
  }, []);

  const handleLeverageCancel = useCallback(() => {
    if (tradeOperationRef.current) {
      setTempLeverage(tradeOperationRef.current.leverage);
    }
    setIsLeverageModalOpen(false);
  }, []);

  const handleWalletTransferConfirm = useCallback(
    async (params: WalletTransferConfirmParams) => {
      if (!mnemonic) {
        toast.error("Mnemonic required", {
          description: "Mnemonic is required to perform transfers.",
          action: createErrorCopyAction("Mnemonic required"),
        });
        return;
      }
      if (!exchangeClient || !exchangeReady) {
        toast.error("Exchange client not ready", {
          description: "Please wait for the exchange client to initialize.",
          action: createErrorCopyAction("Exchange client not ready"),
        });
        return;
      }
      setIsTransferring(true);
      const toastId = toast.loading("Processing transfer...", {
        description: "Sending tokens to address",
      });
      loadingToastIdRef.current = typeof toastId === "number" ? toastId : 0;
      try {
        const result = await exchangeClient.bankTransfer({
          to: params.to,
          assetId: params.assetId,
          amount: params.amount,
          memo: params.memo,
        });
        if (loadingToastIdRef.current !== 0) {
          toast.dismiss(loadingToastIdRef.current);
          loadingToastIdRef.current = 0;
        }
        if (result.success) {
          toast.success("Transfer completed", {
            description: `Successfully sent ${formatCurrency(params.amount)} ${params.assetId} to ${params.to.slice(0, 10)}...${result.txhash ? `\ntxhash: ${result.txhash}` : ""}`,
          });
          setIsWalletTransferModalOpen(false);
          setWalletTransferTo("");
          setWalletTransferAsset("USDC");
          setWalletTransferAmount(10);
          setWalletTransferMemo("");
          refreshBucketBalance();
        } else {
          toast.error("Transfer failed", {
            description: result.errorMessage ?? "Unknown error",
            action: createErrorCopyAction(result.errorMessage ?? ""),
          });
        }
      } catch (err) {
        if (loadingToastIdRef.current !== 0) {
          toast.dismiss(loadingToastIdRef.current);
          loadingToastIdRef.current = 0;
        }
        const msg = err instanceof Error ? err.message : "Transfer failed";
        toast.error("Transfer failed", { description: msg, action: createErrorCopyAction(msg) });
        console.error("Wallet transfer error:", err);
      } finally {
        setIsTransferring(false);
      }
    },
    [mnemonic, exchangeClient, exchangeReady, refreshBucketBalance],
  );

  const handleTransferConfirm = useCallback(
    async (params: TransferModalConfirmParams) => {
      if (!mnemonic) {
        const errorMsg =
          "Mnemonic is required to perform transfers. Please provide a mnemonic to enable transfer functionality.";
        toast.error("Mnemonic required", {
          description: errorMsg,
          action: createErrorCopyAction(errorMsg),
        });
        return;
      }
      if (!exchangeClient || !exchangeReady) {
        const errorMsg =
          "Exchange client is still initializing. Please wait a moment and try again.";
        toast.error("Exchange client not ready", {
          description: errorMsg,
          action: createErrorCopyAction(errorMsg),
        });
        return;
      }

      setIsTransferring(true);
      const toastId = toast.loading("Processing transfer...", {
        description: "Please wait while the transfer is being processed",
      });
      loadingToastIdRef.current = typeof toastId === "number" ? toastId : 0;

      try {
        const assetId = "USDC";
        let result;
        let successMessage = "";

        if (params.type === "transfer") {
          const { from, to, amount } = params;
          if (from === "spot-wallet" && to === "spot-wallet") {
            throw new Error("Cannot transfer from spot wallet to spot wallet");
          }
          if (from === "spot-wallet") {
            if (!to || to === "spot-wallet") {
              throw new Error("Please select a destination bucket");
            }
            result = await exchangeClient.extractToBucket({
              bucketId: to,
              assetId,
              amount: amount,
            });
            if (!result.success) {
              const errorMsg =
                (result as ExtractToBucketResponse).errorMessage ||
                (result as ExtractToBucketResponse).error_message ||
                "Failed to transfer to bucket";
              if (loadingToastIdRef.current !== 0) {
                toast.dismiss(loadingToastIdRef.current);
                loadingToastIdRef.current = 0;
              }
              toast.error("Transfer failed", {
                description: errorMsg,
                action: createErrorCopyAction(errorMsg),
              });
              return;
            }
            const txHash = (result as ExtractToBucketResponse).txhash || "";
            successMessage = `Successfully transferred ${formatCurrency(amount)} from Spot Wallet to ${to}${txHash ? `\nTxHash: ${txHash}` : ""}`;
          } else if (to === "spot-wallet") {
            result = await exchangeClient.withdrawFromBucket({
              bucketId: from,
              assetId,
              amount: amount,
            });
            if (!result.success) {
              const errorMsg =
                (result as WithdrawFromBucketResponse).errorMessage ||
                (result as WithdrawFromBucketResponse).error_message ||
                "Failed to withdraw from bucket";
              if (loadingToastIdRef.current !== 0) {
                toast.dismiss(loadingToastIdRef.current);
                loadingToastIdRef.current = 0;
              }
              toast.error("Withdraw failed", {
                description: errorMsg,
                action: createErrorCopyAction(errorMsg),
              });
              return;
            }
            const txHash = (result as WithdrawFromBucketResponse).txhash ?? "";
            successMessage = `Successfully transferred ${formatCurrency(amount)} from ${from} to Spot Wallet${txHash ? `\nTxHash: ${txHash}` : ""}`;
          } else {
            if (!to) {
              throw new Error("Please select a destination bucket");
            }
            result = await exchangeClient.bucketTransfer({
              fromBucketId: from,
              toBucketId: to,
              assetId,
              amount: amount,
            });
            if (!result.success) {
              const errorMsg =
                (result as BucketTransferResponse).errorMessage ||
                (result as BucketTransferResponse).error_message ||
                (result as BucketTransferResponse).message ||
                "Failed to transfer between buckets";
              if (loadingToastIdRef.current !== 0) {
                toast.dismiss(loadingToastIdRef.current);
                loadingToastIdRef.current = 0;
              }
              toast.error("Transfer failed", {
                description: errorMsg,
                action: createErrorCopyAction(errorMsg),
              });
              return;
            }
            const txHash = (result as BucketTransferResponse).txhash ?? "";
            successMessage = `Successfully transferred ${formatCurrency(amount)} from ${from} to ${to}${txHash ? `\nTxHash: ${txHash}` : ""}`;
          }
        } else {
          const { from, collateralAsset, marginType, amount } = params;
          const existingBucket = findDormantOrEmptyBucket(marginType, collateralAsset);
          let targetBucketId: string;

          if (existingBucket) {
            targetBucketId = existingBucket.bucketId;
            successMessage = `Using existing ${marginType} ${collateralAsset} bucket: ${targetBucketId}`;
          } else {
            const createResult = await exchangeClient.createBucket({
              collateralAssetIndex: assetSymbolToCollateralIndex(collateralAsset),
              marginType: marginType,
              initialMargin: "0",
            });
            if (!createResult.success) {
              throw new Error(createResult.message || "Failed to create bucket");
            }
            targetBucketId = createResult.bucketId ?? "";
            successMessage = `Successfully created ${marginType} margin bucket (${collateralAsset})`;
          }

          if (amount > 0 && !existingBucket) {
            await new Promise((resolve) => setTimeout(resolve, 1500));
          }
          if (amount > 0 && from === "spot-wallet") {
            const extractResult = await exchangeClient.extractToBucket({
              bucketId: targetBucketId,
              assetId: collateralAsset,
              amount: amount,
            });
            if (!extractResult.success) {
              const err =
                extractResult.errorMessage ||
                extractResult.error_message ||
                "Unknown error";
              throw new Error(
                `${existingBucket ? "Bucket found" : "Bucket created"} but failed to transfer funds: ${err}`,
              );
            }
            const txHash = extractResult.txhash ?? "";
            successMessage = `${existingBucket ? "Using existing" : "Successfully created"} ${marginType} margin bucket (${collateralAsset}) and transferred ${formatCurrency(amount)}${txHash ? `\nTxHash: ${txHash}` : ""}`;
          } else if (amount > 0 && from !== "spot-wallet") {
            const transferResult = await exchangeClient.bucketTransfer({
              fromBucketId: from,
              toBucketId: targetBucketId,
              assetId: collateralAsset,
              amount: amount,
            });
            if (!transferResult.success) {
              const err =
                transferResult.errorMessage ||
                transferResult.error_message ||
                transferResult.message ||
                "Unknown error";
              throw new Error(
                `${existingBucket ? "Bucket found" : "Bucket created"} but failed to transfer funds: ${err}`,
              );
            }
            const txHash = transferResult.txhash ?? "";
            successMessage = `${existingBucket ? "Using existing" : "Successfully created"} ${marginType} margin bucket (${collateralAsset}) and transferred ${formatCurrency(amount)}${txHash ? `\nTxHash: ${txHash}` : ""}`;
          }
        }

        if (loadingToastIdRef.current !== 0) {
          toast.dismiss(loadingToastIdRef.current);
          loadingToastIdRef.current = 0;
        }
        toast.success("Transfer completed", {
          description: successMessage,
        });
        setIsTransferModalOpen(false);
        setTransferFrom("");
        setTransferTo("");
        setTransferAmount(10);
        setCreateBucketAmount(10);
        setCreateBucketAsset("");
        setCreateMarginType("cross");
        setTransferMarginType("cross");
        await new Promise((resolve) => setTimeout(resolve, 1000));
        refreshBucketBalance();
      } catch (err) {
        if (loadingToastIdRef.current !== 0) {
          toast.dismiss(loadingToastIdRef.current);
          loadingToastIdRef.current = 0;
        }
        const errorMessage =
          err instanceof Error ? err.message : "Transfer failed";
        toast.error("Transfer failed", {
          description: errorMessage,
          action: createErrorCopyAction(errorMessage),
        });
        console.error("Transfer error:", err);
      } finally {
        setIsTransferring(false);
      }
    },
    [
      mnemonic,
      exchangeClient,
      exchangeReady,
      refreshBucketBalance,
      findDormantOrEmptyBucket,
      assetSymbolToCollateralIndex,
    ],
  );

  const transferOptions = useMemo(() => {
    const bucketOptions = collections
      .filter((c) => c?.bucketId && c.bucketId.trim() !== "")
      .map((collection: Bucket) => ({
        value: collection.bucketId,
        label: `${collection.bucketId} (${formatCurrency(collection.totalEquity)})`,
      }));
    return [
      {
        value: "spot-wallet",
        label: `Spot Wallet (${formatCurrency(spotWalletBalance)})`,
      },
      ...bucketOptions,
    ];
  }, [collections, spotWalletBalance]);

  const bucketsData = useMemo(() => {
    return collections
      .filter((c) => c?.bucketId && c.bucketId.trim() !== "")
      .map((collection: Bucket) => ({
        bucketId: collection.bucketId,
        totalEquity: collection.totalEquity,
      }));
  }, [collections]);

  const isSyncingFromStorageRef = useRef(false);
  useEffect(() => {
    if (
      sessionToken &&
      sessionToken.trim() !== "" &&
      !isSyncingFromStorageRef.current
    ) {
      const stored = localStorage.getItem("authToken");
      if (stored !== sessionToken) {
        localStorage.setItem("authToken", sessionToken);
      }
    }
    isSyncingFromStorageRef.current = false;
  }, [sessionToken]);

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "authToken" && e.newValue) {
        isSyncingFromStorageRef.current = true;
        setSessionToken(e.newValue);
      }
    };
    window.addEventListener("storage", handleStorageChange);
    const interval = setInterval(() => {
      const storedAuthToken = localStorage.getItem("authToken");
      setSessionToken((currentToken) => {
        if (storedAuthToken && storedAuthToken !== currentToken) {
          isSyncingFromStorageRef.current = true;
          return storedAuthToken;
        }
        return currentToken;
      });
    }, 5000);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  const handleLayoutMount = useCallback(() => {
    setIsLayoutMounted(true);
  }, []);

  useEffect(() => {
    const isReady =
      isLayoutMounted &&
      !loadingMarkets &&
      markets.length > 0 &&
      selectedMarketId &&
      !hasTriggeredToggleRef.current;

    if (isReady) {
      hasTriggeredToggleRef.current = true;
      setTimeout(() => {
        toggleEnabled();
      }, 100);
    }
  }, [
    isLayoutMounted,
    loadingMarkets,
    markets.length,
    selectedMarketId,
    toggleEnabled,
  ]);

  const onLayoutChange = useCallback(
    (newLayout: Layout[], _layouts: ResponsiveLayouts) => {
      if (CONFIG.allStatic) return;
      const prevLayout = prevLayoutRef.current;
      const newLayoutItems = newLayout as LayoutItem[];
      if (prevLayout.length !== newLayoutItems.length) {
        prevLayoutRef.current = newLayoutItems;
        setLayout(newLayoutItems);
        return;
      }
      let hasChanged = false;
      for (let i = 0; i < prevLayout.length; i++) {
        const prev = prevLayout[i] as unknown as { i: string; x: number; y: number; w: number; h: number };
        const next = newLayoutItems[i] as unknown as { i: string; x: number; y: number; w: number; h: number };
        if (
          prev.x !== next.x ||
          prev.y !== next.y ||
          prev.w !== next.w ||
          prev.h !== next.h ||
          prev.i !== next.i
        ) {
          hasChanged = true;
          break;
        }
      }
      if (hasChanged) {
        prevLayoutRef.current = newLayoutItems;
        setLayout(newLayoutItems);
      }
    },
    [],
  );

  useEffect(() => {
    if (CONFIG.fullWidth) {
      document.body.classList.add("full-width-screen");
    } else {
      document.body.classList.remove("full-width-screen");
    }
    return () => document.body.classList.remove("full-width-screen");
  }, []);

  const currentMarketId: string = selectedMarketId || "";

  const slotDimensions = useMemo(() => {
    if (layout.length === 0) return {};
    const dimensions: {
      [slotIndex: number]: { width: string; height: string };
    } = {};
    for (let slotIndex = 0; slotIndex <= 8; slotIndex++) {
      const layoutItem = layout.find((item) => {
        const itemLayout = item as unknown as { i: string };
        return itemLayout.i === slotIndex.toString();
      });
      if (layoutItem) {
        const itemLayout = layoutItem as unknown as { w: number; h: number };
        const pixelHeight =
          itemLayout.h * ROW_HEIGHT +
          (itemLayout.h - 1) * CONFIG.layoutMargin[1];
        const widthPercent = (itemLayout.w / COLS) * 100;
        dimensions[slotIndex] = {
          width: `${widthPercent}%`,
          height: `${pixelHeight}px`,
        };
      }
    }
    return dimensions;
  }, [layout]);

  const handleCopyLayoutMatrix = useCallback(async () => {
    try {
      // Sort layout items by slot index (i) and format as [x, y, w, h] arrays
      const layoutMatrix = layout
        .slice()
        .sort((a, b) => {
          const aLayout = a as any;
          const bLayout = b as any;
          const aIndex = parseInt(aLayout.i, 10);
          const bIndex = parseInt(bLayout.i, 10);
          return aIndex - bIndex;
        })
        .map((item) => {
          const itemLayout = item as any;
          return `  [${itemLayout.x}, ${itemLayout.y}, ${itemLayout.w}, ${itemLayout.h}]`;
        })
        .join(",\n");

      const formattedOutput = `[\n${layoutMatrix},\n]`;
      await navigator.clipboard.writeText(formattedOutput);
      alert("Layout matrix copied to clipboard!");
    } catch (err) {
      console.error("Failed to copy layout matrix:", err);
      alert("Failed to copy layout matrix to clipboard");
    }
  }, [layout]);


  // Memoize stringifyLayout to prevent recalculation on every render
  const stringifyLayout = useMemo(() => {
    return layout.map(function (l) {
      const layoutItem = l as any;
      return (
        <div className="layoutItem" key={layoutItem.i}>
          <b>{layoutItem.i}</b>: [{layoutItem.x}, {layoutItem.y}, {layoutItem.w}
          , {layoutItem.h}]
        </div>
      );
    });
  }, [layout]);

  const handleOrderbookPriceClick = useCallback((price: number) => {
    if (tradeOperationRef.current) {
      tradeOperationRef.current.setPrice(price);
    }
  }, []);

  const demoComponents: React.ReactNode[] = useMemo(
    () => [
      <TickerBar
      key={`ticker-${currentMarketId}`}
      baseURL={CONFIG.baseURL}
      selectedMarketId={currentMarketId}
      height={slotDimensions[0]?.height}
    />,
      <TradingViewWidget
        key="tv"
        symbol={CONFIG.tradingViewSymbol}
        interval={CONFIG.tradingViewInterval}
        isCollapsed={false}
      />,
      <Orderbook002
        key={`clobx-${currentMarketId}`}
        baseURL={CONFIG.baseURL}
        marketId={currentMarketId}
        maxDepth={CONFIG.orderbookMaxDepth}
        height={slotDimensions[2]?.height}
        onPriceClick={handleOrderbookPriceClick}
        enhancementConfig={CONFIG.orderbookEnhancement}
      />,
      <TradePrice
        key={`price-${currentMarketId}`}
        baseURL={CONFIG.baseURL}
        marketId={currentMarketId}
      />,
      <TradeOperation
        ref={tradeOperationRef}
        key={`trade-${currentMarketId}`}
        baseURL={CONFIG.baseURL}
        mnemonic={mnemonic ?? DEFAULT_MNEMONIC}
        marketId={currentMarketId}
        selectedBucketBalance={selectedBucketBalance}
        selectedBucketId={selectedBucketId}
        authToken={sessionToken}
        walletAddress={walletAddress ?? ""}
        onLeverageClick={() => {
          if (tradeOperationRef.current) {
            setTempLeverage(tradeOperationRef.current.leverage);
          }
          setIsLeverageModalOpen(true);
        }}
        onNumberCalculatorClick={() => {
          if (tradeOperationRef.current) {
            setNumberCalculatorValue(tradeOperationRef.current.getSizeInput());
            setNumberCalculatorMax(
              tradeOperationRef.current.getAvailableTradeAmount(),
            );
            setIsNumberCalculatorOpen(true);
          }
        }}
      />,
      <TabSection
      baseURL={CONFIG.baseURL}
      authToken={sessionToken}
      walletAddress={walletAddress}
      mnemonic={mnemonic}
      height="400px"
      activeTab={tabSectionActiveTab}
      onTabChange={setTabSectionActiveTab}
      onBucketSelect={(bucketId: string, totalEquity: number) => {
        setSelectedBucketId(bucketId);
        setSelectedBucketBalance(totalEquity);
      }}
      selectedBucketId={selectedBucketId}
      onRequestOpenTransferModal={handleTransferClick}
      onRequestOpenWalletTransferModal={handleWalletTransferClick}
      />
    ],
    [
      currentMarketId,
      slotDimensions,
      selectedBucketId,
      selectedBucketBalance,
      handleOrderbookPriceClick,
      handleTransferClick,
      handleWalletTransferClick,
      mnemonic,
      sessionToken,
      walletAddress,
      tabSectionActiveTab,
    ],
  );

  const onboardModalEl = (
    <OnboardModal
      isOpen={showOnboardModal}
      onMnemonicConnect={handleMnemonicConnect}
      onWalletExtensionConnect={handleWalletExtensionConnect}
    />
  );

  if (loadingMarkets) {
    return (
      <>
        <div style={{ padding: "2rem", textAlign: "center" }}>
          <div>Loading markets...</div>
        </div>
        {onboardModalEl}
      </>
    );
  }

  const marketsError = getError("markets");
  if (marketsError) {
    return (
      <>
        <div style={{ padding: "2rem", textAlign: "center", color: "#ff6b6b" }}>
          <div>Error loading markets: {marketsError}</div>
        </div>
        {onboardModalEl}
      </>
    );
  }

  if (markets.length === 0 || !selectedMarketId) {
    return (
      <>
        <div style={{ padding: "2rem", textAlign: "center" }}>
          <div>No markets available.</div>
        </div>
        {onboardModalEl}
      </>
    );
  }

  const networkChainName =
    CONFIG.baseURL.includes("router.morpheum.io")
      ? "Morpheum Mainnet"
      : new URL(CONFIG.baseURL).hostname;

  return (
    <div className="App-dex-content">
      <MenuBar
        brandName="Morpheum"
        walletAddress={walletAddress ?? ""}
        networkChainName={networkChainName}
        onSettingsClick={() => setIsSettingsOpen((o) => !o)}
        onConnectClick={() => {}}
        onBucketTransfer={() => {
          setTabSectionActiveTab("buckets");
          handleTransferClick();
        }}
        onCoinTransfer={handleWalletTransferClick}
        onDisconnect={() => {
          onboardResetAll();
          localStorage.removeItem(ONBOARD_COMPLETED_KEY);
          setShowOnboardModal(true);
        }}
      />
      <SettingsModal
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        state={settingsState}
        onStateChange={setSettingsState}
      />
      {/* Layout Matrix Display – only when layout is editable (not all static) */}
      {!CONFIG.allStatic && (
        <div className="overview-layout layoutJSON">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "0.5rem",
            }}
          >
            <div>
              Current Breakpoint (from ShowcaseLayout): {currentBreakpoint} (
              {CURRENT_COLS[currentBreakpoint] ?? "—"} columns)
            </div>
            <div>Compaction type: {capitalize(CONFIG.compactType ?? "") || "No Compaction"}</div>
            <span>
              Displayed as <code>[x, y, w, h]</code>
            </span>
            <button
              onClick={handleCopyLayoutMatrix}
              style={{
                padding: "0.5rem 1rem",
                fontSize: "0.9rem",
                borderRadius: "4px",
                border: "1px solid #313131",
                backgroundColor: "#ccc",
                color: "black",
                cursor: "pointer",
                transition: "background-color 0.2s",
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.color = "white";
                e.currentTarget.style.backgroundColor = "#0056b3";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.color = "black";
                e.currentTarget.style.backgroundColor = "#ccc";
              }}
            >
              Copy
            </button>
          </div>
          <div className="columns">{stringifyLayout}</div>
        </div>
      )}
      <ShowcaseLayout
        ref={showcaseLayoutRef}
        margin={CONFIG.layoutMargin}
        onLayoutChange={onLayoutChange}
        onBreakpointChange={setCurrentBreakpoint}
        slotContent={demoComponents}
        compactType={CONFIG.compactType}
        allStatic={CONFIG.allStatic}
        onCompactTypeChange={() => {}}
        onToggleStatic={() => {}}
        onNewLayout={() => {}}
        initialLayout={initialLayout}
        onMount={handleLayoutMount}
      />
      {/* Leverage Selection Modal */}
      <LeverageModal
        isOpen={isLeverageModalOpen}
        currentLeverage={tradeOperationRef.current?.leverage ?? 10}
        tempLeverage={tempLeverage}
        maxRatio={
          tradeOperationRef.current
            ? tradeOperationRef.current.calculateMaxRatio(tempLeverage)
            : tempLeverage.toFixed(2)
        }
        onLeverageChange={setTempLeverage}
        onConfirm={async () => {
          if (
            !selectedBucketId ||
            selectedBucketId === "--" ||
            selectedBucketId.trim() === ""
          ) {
            toast.error("Bucket Required", {
              description: "Please select a bucket before changing leverage.",
            });
            return;
          }
          if (tradeOperationRef.current) {
            setIsLeverageModalOpen(false);
            try {
              await tradeOperationRef.current.changeLeverage(tempLeverage, {
                bucketId: selectedBucketId,
              });
            } catch {
              if (tradeOperationRef.current) {
                setTempLeverage(tradeOperationRef.current.leverage);
              }
            }
          } else {
            setIsLeverageModalOpen(false);
          }
        }}
        onCancel={handleLeverageCancel}
      />

      <NumberPad
        isOpen={isNumberCalculatorOpen}
        value={numberCalculatorValue}
        min={0}
        max={numberCalculatorMax}
        onConfirm={(value) => {
          if (tradeOperationRef.current) {
            tradeOperationRef.current.setSizeInput(value);
          }
          setIsNumberCalculatorOpen(false);
        }}
        onCancel={() => setIsNumberCalculatorOpen(false)}
      />

      <TransferModal
        isOpen={isTransferModalOpen}
        fromValue={transferFrom}
        toValue={transferTo}
        options={transferOptions}
        bucketsData={bucketsData}
        transferAmount={transferAmount}
        createBucketAmount={createBucketAmount}
        createBucketAsset={createBucketAsset}
        createMarginType={createMarginType}
        transferMarginType={transferMarginType}
        onFromChange={setTransferFrom}
        onToChange={setTransferTo}
        onTransferAmountChange={setTransferAmount}
        onCreateBucketAmountChange={setCreateBucketAmount}
        onCreateBucketAssetChange={setCreateBucketAsset}
        onCreateMarginTypeChange={setCreateMarginType}
        onTransferMarginTypeChange={setTransferMarginType}
        onConfirm={handleTransferConfirm}
        onCancel={handleTransferCancel}
        spotWalletBalance={spotWalletBalance}
        activeTab={transferActiveTab}
        onActiveTabChange={setTransferActiveTab}
        isTransferring={isTransferring}
        onOpen={refreshBucketBalance}
        collections={collections.map((c) => ({
          bucketId: c.bucketId,
          marginType: c.marginType,
        }))}
      />
      <WalletTransferModal
        isOpen={isWalletTransferModalOpen}
        toAddress={walletTransferTo}
        assetId={walletTransferAsset}
        amount={walletTransferAmount}
        availableBalance={walletTransferBalance}
        onToAddressChange={setWalletTransferTo}
        onAssetChange={setWalletTransferAsset}
        onAmountChange={setWalletTransferAmount}
        onConfirm={handleWalletTransferConfirm}
        onCancel={handleWalletTransferCancel}
        isTransferring={isTransferring}
        onOpen={fetchWalletTransferBalance}
        memo={walletTransferMemo}
        onMemoChange={setWalletTransferMemo}
      />
      {onboardModalEl}
    </div>
  );
}

export default function LayoutOnlyPage() {
  return (
    <SubscriptionProvider defaultWsURL={CONFIG.wsURL}>
      <LayoutOnlyContent />
    </SubscriptionProvider>
  );
}
